import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, from, concatMap, takeWhile, last, of } from 'rxjs';
import { inject, Injector, runInInjectionContext } from '@angular/core';

/**
 * Type alias for an asynchronous route guard function that can return synchronous or asynchronous results.
 *
 * @param route The activated route snapshot.
 * @param state The router state snapshot.
 *
 * @returns Boolean, UrlTree, or these values wrapped in an Observable or Promise.
 */
export type AsyncGuard = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree;

/**
 * Creates a composite route guard that executes multiple guards sequentially.
 * Stops at the first guard that returns false or a UrlTree.
 *
 * @param guards An array of CanActivateFn guards to execute in order.
 *
 * @returns A CanActivateFn that returns the result of the last executed guard. If any guard returns false or a UrlTree, that result is
 * returned immediately without executing subsequent guards.
 */
export const orderedAsyncGuards = (guards: CanActivateFn[]): CanActivateFn => {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const injector = inject(Injector);

    return from(guards).pipe(
      concatMap((guard) => {
        const result = runInInjectionContext(injector, () => guard(route, state));

        if (result instanceof Observable) {
          return result;
        }

        if (result instanceof Promise) {
          return from(result);
        }

        return of(result);
      }),
      takeWhile(value => value === true, true),
      last()
    );
  };
};
